import { Suspense, useState } from "react";
import {
  CircularProgress,
  List,
  PaperProps,
  styled,
  Typography,
} from "@mui/material";
import { Form, Formik, FormikHelpers } from "formik";
import { ErrorBoundary } from "react-error-boundary";
import { useTranslation } from "react-i18next";

import { useSearchForm } from "hooks/searchHooks";

import { LayerSearchFormValues, LayerProperties } from "types";

import { FlexCentered } from "components/layout/FlexCenterd";
import { PaddedPaper } from "components/surfaces/PaddedPaper";
import { LayerSearchAttributeSelect } from "./LayerSearchAttributeSelect";
import { LayerSearchLayerSelect } from "./LayerSearchLayerSelect";
import { LayerSearchQueryField } from "./LayerSearchQueryField";
import { LayerSearchResults } from "./LayerSearchResults";

interface Props extends PaperProps {
  config: string;
  layerProperties: LayerProperties[];
}

export function LayerSearchForm(props: Props) {
  const { config, layerProperties, ...paperProps } = props;
  const { t } = useTranslation();
  const [searchFormValues, updateSearchForm] = useSearchForm();
  const [selection, setSelection] = useState<LayerSearchFormValues>({
    layer: searchFormValues.layer,
    attributes: searchFormValues.attributes,
    query: searchFormValues.query,
  });

  const initialValues: LayerSearchFormValues = {
    layer: selection.layer || "",
    attributes: selection.attributes || [],
    query: selection.query,
  };

  async function onSubmit(
    values: LayerSearchFormValues,
    helpers: FormikHelpers<LayerSearchFormValues>
  ) {
    const { setSubmitting } = helpers;
    setSubmitting(true);
    setSelection({ ...values });

    updateSearchForm(values.layer, values.query, values.attributes);

    return Promise.resolve();
  }

  return (
    <Styles variant="defaultBackground" {...paperProps}>
      <Formik initialValues={initialValues} onSubmit={onSubmit}>
        {() => (
          <Form>
            <Suspense
              fallback={
                <FlexCentered>
                  <CircularProgress />
                </FlexCentered>
              }
            >
              <LayerSearchLayerSelect
                config={config}
                className="form-field"
                layerProperties={layerProperties}
              />
            </Suspense>
            <Suspense
              fallback={
                <FlexCentered>
                  <CircularProgress />
                </FlexCentered>
              }
            >
              <LayerSearchAttributeSelect
                config={config}
                layerProperties={layerProperties}
                className="form-field"
              />
            </Suspense>
            <LayerSearchQueryField className="form-field" />
            <List>
              {selection && (
                <ErrorBoundary
                  key={selection.query}
                  fallbackRender={() => (
                    <Typography color="error">
                      {t("label.noResults")}
                    </Typography>
                  )}
                >
                  <Suspense
                    fallback={
                      <FlexCentered>
                        <CircularProgress />
                      </FlexCentered>
                    }
                  >
                    <LayerSearchResults
                      config={config}
                      selection={selection}
                      layerProperties={layerProperties}
                    />
                  </Suspense>
                </ErrorBoundary>
              )}
            </List>
          </Form>
        )}
      </Formik>
    </Styles>
  );
}

const Styles = styled(PaddedPaper)`
  .form-field {
    & > .placeholder-label {
      min-height: ${({ theme }) => theme.spacing(7)};
      border-bottom: 1px solid rgba(255, 255, 255, 0.23);
    }
    & + .form-field {
      margin-top: ${({ theme }) => theme.spacing(2)};
    }

    &:last-child {
      margin-bottom: ${({ theme }) => theme.spacing(2)};
    }
  }
`;
